import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { GridApiPro } from '@mui/x-data-grid-pro/models'
import { GridColDef } from '@mui/x-data-grid/models'
import { NoteListItem } from '@finance-lab/utils/types'
import { NoteEntityRef, NotesApi } from '../../api/NotesApi'
import { formatDateYYYYMMDDhhmm } from '../../packages/utils/numberFormatters'
import { useAppLocale } from '../../i18n/localeUtils'
import { useNoteEncryption } from '../../hooks/useNoteEncryption'
import useAuth from '../../hooks/useAuth'
import useConfirmationDialog from '../../components/dialogs/ConfirmationDialog'
import { NoteEditorDialog } from '../components/NoteEditorDialog'
import { NoteEntityLink } from './NoteEntityLink'
import { useTranslation } from 'react-i18next'
import useTheme from '@mui/system/useTheme'
import { gridClasses } from '@mui/x-data-grid/constants'

const notesApi = new NotesApi()
const NOTE_PREVIEW_LENGTH = 200

type NoteRow = NoteListItem & {
    decryptedContent: string
}

const NoteContentCell = ({
    content,
    expandAll,
    t,
}: {
    content: string
    expandAll: boolean
    t: (key: string) => string
}) => {
    const [expanded, setExpanded] = useState(false)
    const isTruncated = content.length > NOTE_PREVIEW_LENGTH
    const showFull = expandAll || expanded || !isTruncated
    const displayContent = showFull ? content : `${content.slice(0, NOTE_PREVIEW_LENGTH).trimEnd()}…`

    return (
        <Box sx={{ py: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {displayContent}
            {isTruncated && !expandAll ? (
                <Button
                    onClick={(event) => {
                        event.stopPropagation()
                        setExpanded((value) => !value)
                    }}
                    size='small'
                    sx={{ display: 'block', mt: 0.5, px: 0 }}
                >
                    {expanded ? t('site.actions.showLess') : t('site.actions.showMore')}
                </Button>
            ) : null}
        </Box>
    )
}

const toEntityRef = (item: NoteListItem): NoteEntityRef => {
    if (item.entityType === 'Issue') {
        return { issueId: item.entityId }
    }
    if (item.entityType === 'Warrant') {
        return { warrantId: item.entityId }
    }
    return { convertibleId: item.entityId }
}

export const NotesView = () => {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    const { user } = useAuth()
    const { confirm, ConfirmationDialog } = useConfirmationDialog()
    const theme = useTheme()
    const { decrypt, error: cryptoError, isLoading: isCryptoLoading, isReady } = useNoteEncryption()
    const [notes, setNotes] = useState<NoteRow[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [editingEntityRef, setEditingEntityRef] = useState<NoteEntityRef | null>(null)
    const [expandAllNotes, setExpandAllNotes] = useState(false)
    const apiRef = useRef<GridApiPro | null>(null)

    const hasTruncatedNotes = notes.some((note) => note.decryptedContent.length > NOTE_PREVIEW_LENGTH)
    const loadNotes = useCallback(async () => {
        if (!isReady) {
            return
        }

        setIsLoading(true)
        setLoadError(null)

        try {
            const result = await notesApi.listNotes()
            const rows = await Promise.all(
                result.map(async (note) => {
                    let decryptedContent = ''

                    if (note.content) {
                        try {
                            decryptedContent = await decrypt(note.content)
                        } catch (err) {
                            console.error(err)
                            decryptedContent = t('pages.notes.errors.couldNotRead')
                        }
                    }

                    return {
                        ...note,
                        decryptedContent,
                    }
                }),
            )

            setNotes(rows)
        } catch (err) {
            console.error(err)
            setLoadError(t('pages.notes.errors.couldNotLoad'))
            setNotes([])
        } finally {
            setIsLoading(false)
        }
    }, [decrypt, isReady, t])

    const handleDelete = useCallback(
        async (note: NoteRow) => {
            const confirmed = await confirm(t('pages.notes.deleteConfirm.message'), {
                title: t('pages.notes.deleteConfirm.title'),
                confirmLabel: t('pages.notes.deleteConfirm.confirmLabel'),
            })
            if (!confirmed) {
                return
            }

            try {
                await notesApi.deleteNote(toEntityRef(note))
                await loadNotes()
            } catch (err) {
                console.error(err)
                setLoadError(t('pages.notes.errors.couldNotDelete'))
            }
        },
        [confirm, loadNotes, t],
    )

    useEffect(() => {
        if (!isReady) {
            return
        }

        void loadNotes()
    }, [isReady, loadNotes])

    useEffect(() => {
        apiRef.current?.resetRowHeights()
    }, [expandAllNotes, notes])

    const columns = useMemo<GridColDef<NoteRow>[]>(
        () => [
            {
                field: 'name',
                headerName: t('pages.notes.columns.name'),
                flex: 1,
                minWidth: 240,
                sortable: false,
                renderCell: ({ row }) => <NoteEntityLink item={row} />,
            },
            {
                field: 'updatedAt',
                headerName: t('pages.notes.columns.updated'),
                width: 134,
                valueFormatter: (value) => formatDateYYYYMMDDhhmm(value, { locale }),
            },
            {
                field: 'decryptedContent',
                headerName: t('pages.notes.columns.note'),
                flex: 2,
                minWidth: 280,
                sortable: false,
                renderCell: ({ value }) => (
                    <NoteContentCell content={value ?? ''} expandAll={expandAllNotes} t={t} />
                ),
            },
            {
                field: 'actions',
                headerName: '',
                width: 74,
                sortable: false,
                renderCell: ({ row }) => (
                    <Stack direction='row' spacing={0.5}>
                        <Tooltip title={t('site.actions.edit')}>
                            <span>
                                <IconButton
                                    aria-label={t('site.actions.edit')}
                                    disabled={!!cryptoError || isCryptoLoading}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setEditingEntityRef(toEntityRef(row))
                                    }}
                                    size='small'
                                >
                                    <EditIcon color='warning' />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('site.actions.delete')}>
                            <span>
                                <IconButton
                                    aria-label={t('site.actions.delete')}
                                    disabled={!!cryptoError || isCryptoLoading}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        void handleDelete(row)
                                    }}
                                    size='small'
                                >
                                    <DeleteIcon color='error' />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [cryptoError, expandAllNotes, handleDelete, isCryptoLoading, locale, t],
    )

    if (!user?.groups?.includes('superadmin')) {
        return null
    }

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    return (
        <>
            <ConfirmationDialog />
            <Helmet title={t('pages.notes.title')} />
            <Typography variant='h3' gutterBottom>
                {t('pages.notes.title')}
            </Typography>
            <Stack
                direction='row'
                sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between', mb: 4 }}
            >
                <Typography color='text.secondary'>{t('pages.notes.description')}</Typography>
                {hasTruncatedNotes ? (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={expandAllNotes}
                                onChange={(event) => setExpandAllNotes(event.target.checked)}
                                disableFocusRipple
                                disableRipple
                                size='small'
                                sx={{ marginLeft: 0, paddingLeft: 0 }}
                            />
                        }
                        label={t('pages.notes.expandAll')}
                        sx={{ m: 0 }}
                    />
                ) : null}
            </Stack>

            {cryptoError ? (
                <Alert severity='error' sx={{ mb: 2 }}>
                    {cryptoError}
                </Alert>
            ) : null}
            {loadError ? (
                <Alert severity='warning' sx={{ mb: 2 }}>
                    {loadError}
                </Alert>
            ) : null}

            <DataGridPro
                apiRef={apiRef}
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    '& .odd': {
                        // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                    },
                    '& .MuiDataGrid-cell': {
                        paddingY: 4,
                        alignItems: 'center',
                        display: 'flex',
                    },
                    '& .MuiDataGrid-cell[data-field="actions"], & .MuiDataGrid-columnHeader[data-field="actions"]': {
                        px: 0,
                    },
                }}
                autoHeight
                columns={columns}
                density='compact'
                disableColumnMenu
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                loading={isLoading || isCryptoLoading}
                pageSizeOptions={[25, 50, 100]}
                pagination
                initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                }}
                rows={notes}
            />

            {editingEntityRef ? (
                <NoteEditorDialog
                    entityRef={editingEntityRef}
                    onClose={() => setEditingEntityRef(null)}
                    onDeleted={() => void loadNotes()}
                    onSaved={() => void loadNotes()}
                    open
                />
            ) : null}
        </>
    )
}
